import {
  collection,
  getDocs,
  query,
  limit,
  orderBy,
  OrderByDirection,
  where,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  startAfter,
  QueryConstraint,
  CollectionReference,
  QuerySnapshot,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';

import Subscription from '../../models/Subscription';
import AI from '../../models/AI';
import { getSubscriptionFilters } from '../../../utils/SubscriptionsUtils';
import { getAIsBatch } from '../../../utils/AIsUtils';
import { chunkArray } from '@leedpulse-monorepo/utils';
import { EProductType } from '@leedpulse-monorepo/enums';
import SubscriptionDataSource from '../../enums/subscription/subscription_datasource/SubscriptionDataSource';

export interface SubscriptionFilters {
  employeesInCharge?: string[],
  status?: SubscriptionStatus,
  plans?: string[],
  subStatus?: string,
  displayPartnerAccounts?: boolean,
  crmUsed?: SubscriptionDataSource[],
  products?: EProductType[]
};

export type SubscriptionStatus = 'expired' | 'active';

const FIRESTORE_IN_OPERATOR_LIMIT = 30;

async function getSubscriptionsWithFilters(
  filters: SubscriptionFilters,
  orderByName: string | undefined,
  sortDirection: OrderByDirection | undefined,
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<{
  subscriptions: Subscription[];
  totalCount: number;
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  ais: Map<string, AI>;
}> {
  try {
    const db = firestore;
    const subscriptionsCollection = collection(
      db,
      FirestoreCollections.subscriptions
    );

    // Get plans matching the products if the products filter is present
    let planIdsForProducts: string[] = [];
    if (filters?.products && filters.products.length > 0) {
      const plansCollection = collection(db, FirestoreCollections.plans);
      
      // Get all plans for each product
      const planQueries = filters.products.map(product =>
        getDocs(query(plansCollection, where('product', '==', product)))
      );
      
      const planResults = await Promise.all(planQueries);
      planIdsForProducts = planResults.flatMap(snapshot =>
        snapshot.docs.map(doc => doc.id)
      );
    }

    // If we have plans for the products, add them to the filters
    const filtersWithPlans = { ...filters };
    if (planIdsForProducts.length > 0) {
      // If we already have plans in the filters, compute the intersection
      if (filters.plans && filters.plans.length > 0) {
        filtersWithPlans.plans = filters.plans.filter(id => planIdsForProducts.includes(id));
        // If the intersection is empty, return an empty result
        if (filtersWithPlans.plans.length === 0) {
          return {
            subscriptions: [],
            totalCount: 0,
            lastVisible: lastVisible || {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
            ais: new Map<string, AI>()
          };
        }
      } else {
        filtersWithPlans.plans = planIdsForProducts;
      }
    } else if (filters?.products && filters.products.length > 0) {
      // If no plans found for the requested products, return an empty result
      return {
        subscriptions: [],
        totalCount: 0,
        lastVisible: lastVisible || {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
        ais: new Map<string, AI>()
      };
    }

    const subscriptions: Subscription[] = [];
    const subscriptionConstraints = await getSubscriptionFilters(filtersWithPlans);

    // If we have more than 10 plans, we need to split into chunks (Firestore limit for 'in')
    let results;
    if (filtersWithPlans.plans && filtersWithPlans.plans.length > FIRESTORE_IN_OPERATOR_LIMIT) {
      console.log('plans: ', filtersWithPlans.plans);
      const planChunks = chunkArray(filtersWithPlans.plans, FIRESTORE_IN_OPERATOR_LIMIT);
      const allDocs: QueryDocumentSnapshot<DocumentData, DocumentData>[] = [];

      for (const planChunk of planChunks) {
        const chunkFilters = { ...filtersWithPlans, plans: planChunk };
        const chunkConstraints = await getSubscriptionFilters(chunkFilters);
        const chunkResults = await getSubscriptionDocsWithoutChunks(
          subscriptionsCollection,
          chunkConstraints,
          orderByName,
          sortDirection,
          offset,
          lastVisible
        );
        allDocs.push(...chunkResults.docs);
      }

      // Remove duplicates
      const uniqueDocs = allDocs.filter((doc1, i, arr) =>
        arr.findIndex(doc2 => doc2.data().id === doc1.data().id) === i
      );

      // Sort all docs again by name and direction
      uniqueDocs.sort((a, b) => {
        const valA = a.get(orderByName ?? 'created');
        const valB = b.get(orderByName ?? 'created');

        if (sortDirection === 'asc') {
          return valA < valB ? -1 : valA > valB ? 1 : 0;
        } else {
          return valA > valB ? -1 : valA < valB ? 1 : 0;
        }
      });

      // Limit to offset
      const limitedDocs = uniqueDocs.slice(0, offset);

      results = {
        docs: limitedDocs,
        empty: limitedDocs.length === 0,
        size: limitedDocs.length
      } as QuerySnapshot<DocumentData, DocumentData>;
    } else {
      results = await getSubscriptionDocsWithoutChunks(
        subscriptionsCollection,
        subscriptionConstraints,
        orderByName,
        sortDirection,
        offset,
        lastVisible
      );
    }

    results.docs.forEach((doc: QueryDocumentSnapshot<DocumentData, DocumentData>) => {
      subscriptions.push(Subscription.fromFirestore(doc));
    });

    const newLastVisible = results.docs[results.docs.length - 1];

    // Fetch AIs in batch for all subscriptions
    const aiIds = subscriptions
      .map(sub => sub.idAi)
      .filter(idAi => idAi && idAi.trim() !== '');
    const ais = await getAIsBatch(aiIds);

    // Create a Map subscriptionId -> AI for easier access
    const subscriptionAIs = new Map<string, AI>();
    subscriptions.forEach(sub => {
      if (sub.idAi && ais.has(sub.idAi)) {
        const ai = ais.get(sub.idAi);
        if (ai) {
          subscriptionAIs.set(sub.id, ai);
        }
      }
    });

    const subscriptionsSnapshot = await getCountFromServer(
      query(
        subscriptionsCollection
      )
    );

    return {
      subscriptions,
      totalCount: subscriptionsSnapshot.data().count ?? 0,
      lastVisible: newLastVisible,
      ais: subscriptionAIs
    };
  } catch (e) {
    console.log('error: ', e);
    throw new GetSubscriptionsWithFiltersFailure();
  }
}

async function getSubscriptionDocsWithoutChunks(
  subscriptionsCollection: CollectionReference,
  subscriptionConstraints: QueryConstraint[],
  orderByName: string | undefined,
  sortDirection: OrderByDirection | undefined,
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>
) {
  const constraints: QueryConstraint[] = [
    ...subscriptionConstraints,
    orderBy(
      orderByName ?? 'created',
      sortDirection ?? 'desc'
    ),
    limit(offset)
  ];

  if (lastVisible) {
    constraints.push(startAfter(lastVisible))
  }

  return await getDocs(
    query(
      subscriptionsCollection,
      ...constraints
    )
  );
};

export class GetSubscriptionsWithFiltersFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getSubscriptionsWithFilters;
